
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, AlertCircle, Calendar as CalendarIcon, RefreshCw, CheckCircle } from "lucide-react";
import { format, addDays, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { usePsychologistPricing } from "@/hooks/usePsychologistPricing";
import DynamicTimeSlotGrid from "@/components/patient/DynamicTimeSlotGrid";

interface NewSchedulingSystemProps {
  psychologistId: string;
  psychologistName: string;
  onSelectSlot?: (date: string, time: string, duration: number, price: number) => void;
}

const NewSchedulingSystem: React.FC<NewSchedulingSystemProps> = ({
  psychologistId,
  psychologistName,
  onSelectSlot
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedDuration, setSelectedDuration] = useState(60);
  
  const { data: pricing, isLoading: pricingLoading } = usePsychologistPricing(psychologistId);

  const handleDateSelect = (date: Date | undefined) => {
    console.log('📅 [NewSchedulingSystem] Data selecionada:', {
      date: date ? date.toISOString() : 'undefined',
      formatted: date ? format(date, 'yyyy-MM-dd') : 'N/A'
    });
    setSelectedDate(date);
  };

  const handleSlotSelect = (time: string, duration: number, price: number) => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    console.log('⏰ [NewSchedulingSystem] Horário selecionado com preço REAL:', { dateStr, time, duration, price });
    onSelectSlot?.(dateStr, time, duration, price);
  };

  const handleDurationChange = (duration: number) => {
    console.log('🕐 [NewSchedulingSystem] Duração mudou para:', duration);
    setSelectedDuration(duration);
  };

  // CORREÇÃO: Usar preços reais do banco com propriedades corretas
  const getDurationOptions = () => {
    if (!pricing) {
      return [
        { duration: 30, price: 150 },
        { duration: 60, price: 280 }
      ];
    }
    
    console.log('💰 [NewSchedulingSystem] Usando preços REAIS:', pricing);
    
    return [
      { duration: 30, price: pricing.price_30min },
      { duration: 60, price: pricing.price_60min }
    ];
  };

  const durationOptions = getDurationOptions();

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Agendar Consulta - {psychologistName}
          </CardTitle>
          <CardDescription>
            Selecione a data, duração e horário desejados
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendário de Seleção */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">1. Selecione a Data</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              disabled={(date) => date < startOfDay(new Date())}
              locale={ptBR}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Seleção de Duração e Preços REAIS */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">2. Duração e Preço</CardTitle>
            {pricingLoading && (
              <CardDescription className="text-blue-600">
                Carregando preços reais...
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              {durationOptions.map((option) => (
                <div
                  key={option.duration}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedDuration === option.duration
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleDurationChange(option.duration)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{option.duration} minutos</span>
                      {selectedDuration === option.duration && (
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                      )}
                    </div>
                    <Badge variant="secondary">
                      R$ {option.price.toFixed(2).replace('.', ',')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            {!pricingLoading && pricing && (
              <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                ✅ Preços carregados do dashboard do psicólogo
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Grid de Horários Disponíveis usando DynamicTimeSlotGrid */}
      {selectedDate && (
        <DynamicTimeSlotGrid
          psychologistId={psychologistId}
          selectedDate={selectedDate}
          selectedDuration={selectedDuration}
          onSelectTimeSlot={handleSlotSelect}
          onDurationChange={handleDurationChange}
        />
      )}
    </div>
  );
};

export default NewSchedulingSystem;
